// Cinema licensing — API docs §14 (Movie Keys).
//
// Films are chain-level objects; both tiers see and interact with them.
// CHAIN: issue, allocate, revoke, update film status.
// BRANCH: return, consume; reads are scoped automatically.
// All key movements are appended to the ledger — nothing is ever deleted.
import api from "../lib/apiClient";

/* --- films (catalogue) --------------------------------------------------- */

export const listMovies = (params = {}) =>
  api.get("/movie-keys/movies", { params }).then(r => r.data);

export const addMovie = (payload) =>
  api.post("/movie-keys/movies", payload).then(r => r.data);

export const getMovie = (id) =>
  api.get(`/movie-keys/movies/${id}`).then(r => r.data);

// CHAIN only — update film details.
export const updateMovie = (id, payload) =>
  api.patch(`/movie-keys/movies/${id}`, payload).then(r => r.data);

// CHAIN only — close or reopen a run.
// Body: status ("ACTIVE"|"CLOSED"), note?
export const setMovieStatus = (id, payload) =>
  api.patch(`/movie-keys/movies/${id}/status`, payload).then(r => r.data);

/* --- key movements ------------------------------------------------------- */

// Declare received keys (from distributor) — CHAIN only.
// Body: movie, keys, note?, clientRef?
export const issueKeys = (payload) =>
  api.post("/movie-keys/issue", payload).then(r => r.data);

// Hand keys down to a branch or employee — CHAIN only.
// Body: movie, holderType (BRANCH|EMPLOYEE), holder, keys, note?, clientRef?
export const allocateKeys = (payload) =>
  api.post("/movie-keys/allocate", payload).then(r => r.data);

// Pull keys back from a direct child — CHAIN only.
// Body: same shape as allocate.
export const revokeKeys = (payload) =>
  api.post("/movie-keys/revoke", payload).then(r => r.data);

// Branch returns keys to chain.
// Body: movie, keys, note?, clientRef?
export const returnKeys = (payload) =>
  api.post("/movie-keys/return", payload).then(r => r.data);

// Consume a key for a show.
// Body: movie, keys? (default 1), holderType?, holder?,
//       show? { date, time, screen }, note?, clientRef?
export const consumeKeys = (payload) =>
  api.post("/movie-keys/consume", payload).then(r => r.data);

// Reverse a prior consumption — keys return to the holding that lost them.
// Body: movement (original CONSUME id), note?, clientRef?
export const restoreKeys = (payload) =>
  api.post("/movie-keys/restore", payload).then(r => r.data);

/* --- reporting ----------------------------------------------------------- */

// All holdings (film × holder rows).
// Query: page, limit, partner, movie, tier, holderType, holder, search,
//        exhausted, unused, hasKeys, sort
export const listKeyHoldings = (params = {}) =>
  api.get("/movie-keys", { params }).then(r => r.data);

// Per-film rollup totals.
// Query: partner, movie, status, search
export const getKeysSummary = (params = {}) =>
  api.get("/movie-keys/summary", { params }).then(r => r.data);

// Distribution tree for one film — who holds how many keys.
// Query: partner
export const getMovieDistribution = (id, params = {}) =>
  api.get(`/movie-keys/movies/${id}/distribution`, { params }).then(r => r.data);

// Full movement ledger.
// Query: page, limit, from, to, date, showDate, partner, movie, type, by, holder, holding
export const listKeyHistory = (params = {}) =>
  api.get("/movie-keys/history", { params }).then(r => r.data);

// Single movement record.
export const getKeyMovement = (id) =>
  api.get(`/movie-keys/history/${id}`).then(r => r.data);

// Shows that played (un-reversed burns grouped by date/film).
// Query: from, to, partner, movie
export const listShows = (params = {}) =>
  api.get("/movie-keys/shows", { params }).then(r => r.data);

// One employee's key holdings across all films.
export const getEmployeeKeys = (employeeId) =>
  api.get(`/movie-keys/employees/${employeeId}`).then(r => r.data);
